import { Button, Input, Select, notification, Row, Col, Modal, Divider } from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { usePermissions } from "../../assets/utils/usePermissions";
import lib, { myRequestObj } from "../../assets/utils/lib";
import { states } from "../../assets/utils/config";
import { CheckOutlined, EditOutlined, LoadingOutlined } from "@ant-design/icons";

import InputCopy from "../compoinents/copyInput.js";
import { renderButton } from "./renderButton.js";
import { same } from "../../assets/utils/Helpers.js";
import {
  toFacilityProviderRecord,
  getFacilityPlanType,
  resolveFacilityTierSelectValue,
  resolveFacilityTypeSelectValue,
} from "../facilities/facilityUtils";

const { TextArea, Search } = Input;
const { Option } = Select;

export const FacilityData = ({ data, display, action, command, key, source = 1, viewOnly = false, startInEditMode = false, modalZIndex, modalClassName }) => {
  const { hasAnyPermissionWithLegacy } = usePermissions();
  const canEdit = () =>
    hasAnyPermissionWithLegacy(
      ['facilities.manage', 'facilities.update', 'admin.*'],
      [1, 2, 4, 5, 6],
    )

  // Callers that already mean "manage/edit this profile" (e.g. Manage profile on
  // the facility detail page) open straight into the editable form — permission
  // still decides, so this can never hand edit rights to a read-only viewer.
  const opensEditable = startInEditMode && canEdit();

  const [n_data, setNdata] = useState(() => toFacilityProviderRecord(data));
  const [edit, setEdit] = useState(opensEditable);
  const [update, setUpdate] = useState(false);

  const [showSetFacilityPassword, setShowSetFacilityPassword] = useState(false);
  const [tierOptions, setTierOptions] = useState([{ label: "All Plans (no minimum)", value: null, tier_name: "all plans" }]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  // Facility types come from the `facility_types` catalog, the same source the
  // onboarding modal and the Facility Types settings page use. This form used
  // a hardcoded list in config.js that had drifted from it: no eye-care type at
  // all, and "Radiology"/"Gymnasium" entries with no matching row, so picking
  // one wrote a type name the API could not resolve to an id.
  const [facilityTypeOptions, setFacilityTypeOptions] = useState([]);
  const [loadingFacilityTypes, setLoadingFacilityTypes] = useState(false);

  const { confirm } = Modal;

  const facilityTypeValue = useMemo(
    () => resolveFacilityTypeSelectValue(n_data, facilityTypeOptions),
    [n_data, facilityTypeOptions],
  );

  const planTierValue = useMemo(
    () => resolveFacilityTierSelectValue(n_data, tierOptions),
    [n_data, tierOptions],
  );

  const planTierPlaceholder = useMemo(() => {
    const plan = getFacilityPlanType(n_data);
    return plan || "Select plan tier";
  }, [n_data]);

  useEffect(() => {
    if (!display) return;
    setNdata(toFacilityProviderRecord(data));
    setEdit(opensEditable);
  }, [display, data, opensEditable]);

  useEffect(() => {
    setUpdate(!same(data, n_data));
  }, [n_data]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   if (display) {
  //     handleFocusOut();
  //   }
  // }, [display]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!display) return;
    let mounted = true;
    const loadTiers = async () => {
      setLoadingTiers(true);
      try {
        const resp = await lib.getAdminTiers({ scope: 'active' });
        const body = resp?.data;
        if (body?.error || !Array.isArray(body?.data?.tiers)) {
          return;
        }
        const options = body.data.tiers
          .filter((tier) => Number.isFinite(Number(tier?.tier_id)))
          .map((tier) => ({
            label: `${tier?.tier_name || `Tier ${tier?.tier_id}`} (Rank: ${tier?.tier_rank ?? '--'})`,
            value: Number(tier?.tier_id),
            tier_name: tier?.tier_name || '',
          }));
        if (mounted) {
          setTierOptions([{ label: "All Plans (no minimum)", value: null, tier_name: "all plans" }, ...options]);
        }
      } finally {
        if (mounted) setLoadingTiers(false);
      }
    };
    const loadFacilityTypes = async () => {
      setLoadingFacilityTypes(true);
      try {
        const resp = await lib.getAdminFacilityTypes();
        const body = resp?.data;
        if (body?.error || !Array.isArray(body?.data)) return;
        const options = body.data
          .filter((type) => Number.isFinite(Number(type?.id)))
          .map((type) => ({
            label: type?.name ?? `Type ${type.id}`,
            value: Number(type.id),
          }));
        if (mounted) setFacilityTypeOptions(options);
      } finally {
        if (mounted) setLoadingFacilityTypes(false);
      }
    };

    loadTiers();
    loadFacilityTypes();
    return () => {
      mounted = false;
    };
  }, [display]);

  const updateDataFacility = async (e, thisData = {}) => {
    const targ = e;
    const actionType = Number(e);
    const loadingMap = {
      1: 'update',
      2: 'activate',
      3: 'disable',
      4: 'flag',
      5: 'revoke',
    };

    setEdit(false);
    setLoadingFlags(loadingMap[actionType], true)

    const resolvedPayload = { ...n_data, ...thisData };
    if (resolvedPayload?.facility_tier_id === '' || resolvedPayload?.facility_tier_id === undefined) {
      resolvedPayload.facility_tier_id = null;
    } else if (resolvedPayload?.facility_tier_id !== null) {
      resolvedPayload.facility_tier_id = Number(resolvedPayload.facility_tier_id);
    }

    lib
      .updateProvider(resolvedPayload)
      .then((res) => res?.data)
      .then((data) => {
        if (!Number(data?.status)) {
          if (Number(targ) === 4) {
            return;
          }

          notification.success({
            message: data?.message,
            placement: "topLeft",
          });

        } else {
          notification.error({
            message: data?.message,
            placement: "topLeft",
          });
        }
      })
      .catch((err) => {
        setEdit(true);
      })
      .finally(() => {
        setLoadingFlags(loadingMap[actionType], false)
        setUpdate(false);
        action();
      });
  };

  const [loadingStates, setLoadingStates] = useState({
    loading: false,
    update: false,
    activate: false,
    disable: false,
  });


  const setLoadingFlags = (key, value) => {
    setLoadingStates(d => ({ ...d, [key]: value }))
  }

  const isLoading = (key) => Object.values(loadingStates).some(Boolean);

  const handleCancel = () => {
    same(data, n_data) ? action() : command()
  }

  return (
    <>
      <Modal
        title={
          <Row justify={'start'} align={'middle'}>
            <Col>{n_data?.facility_name}</Col>
            <Divider type="vertical" />
            <Col>
              {
                renderButton({
                  type: 'link',
                  label: data?.facility_status ? "Active" : "Inactive",
                  danger: !Number(data?.facility_status),
                })
              }
            </Col>
            {
              canEdit() && !opensEditable ? <>
                <Divider type="vertical" style={{ borderColor: 'black' }} />
                <Col style={{ textAlign: "right", alignContent: "flex-end" }}>
                  {renderButton({
                    label: edit ? <>Close Edit</> : <>Edit</>,
                    isDisabled: isLoading(),
                    onClick: () => setEdit(!edit),
                    icon: edit ? <CheckOutlined /> : <EditOutlined />
                  })
                  }
                </Col>
              </> : null
            }
            {hasAnyPermissionWithLegacy(['facilities.manage_staff', 'admin.*'], [1]) ? <>
              <Divider type="vertical" style={{ borderColor: 'black' }} />
              <Col>
                {renderButton({
                  label: 'Set Password',
                  isDisabled: isLoading(),
                  onClick: () => setShowSetFacilityPassword(true)
                })}
              </Col>
            </> : null}
          </Row>
        }
        centered
        open={display}
        onOk={action}
        onCancel={action}
        zIndex={modalZIndex}
        footer={
          <>
            {hasAnyPermissionWithLegacy(
              ['facilities.manage', 'facilities.update', 'admin.*'],
              [1, 2],
            ) ? (
              <>
                {
                  update && renderButton({
                    isDisabled: isLoading(),
                    label: 'Update',
                    loadingState: loadingStates?.update,
                    onClick: () => updateDataFacility(1)
                  })
                }
                {data?.facility_status ? (
                  renderButton({
                    type: 'dashed',
                    label: 'Disable',
                    danger: true,
                    loadingState: loadingStates?.disable,
                    isDisabled: isLoading(),
                    onClick: () => updateDataFacility(3, { facility_status: 0 })
                  })
                ) : (
                  renderButton({
                    type: 'dashed',
                    label: 'Activate',
                    loadingState: loadingStates?.activate,
                    isDisabled: isLoading(),
                    onClick: () => updateDataFacility(2, { facility_status: 1 })
                  })
                )}
              </>
            ) : null}
            {
              renderButton({
                type: 'dashed',
                label: 'Close',
                danger: true,
                isDisabled: isLoading(),
                onClick: handleCancel
              })
            }
          </>
        }
        width={800}
        destroyOnClose
        rootClassName={modalClassName}
        className={modalClassName ? `${modalClassName}__dialog` : undefined}
      >
        <Row style={{ margin: '20px 0px' }}>
          <Col span={24}>
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <Row>
                  <Col span={8}>Facility Name:</Col>
                  <Col span={16}>
                    <TextArea
                      disabled={!edit}
                      value={n_data?.facility_name}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_name: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility Type: </Col>
                  <Col span={16}>
                    <Select
                      disabled={!edit}
                      loading={loadingFacilityTypes}
                      value={facilityTypeValue}
                      onChange={(value) => {
                        // The API resolves either, but sending both means a
                        // rename in the Facility Types catalog cannot orphan
                        // this record from its type.
                        const picked = facilityTypeOptions.find((ft) => ft.value === value);
                        setNdata((d) => ({
                          ...d,
                          facility_type_id: value,
                          facility_type: picked?.label ?? d?.facility_type,
                        }));
                      }}
                      options={facilityTypeOptions}
                      placeholder={loadingFacilityTypes ? "Loading facility types…" : "Select a facility type"}
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility Plans:</Col>
                  <Col span={16}>
                    <Select
                      disabled={!edit}
                      value={planTierValue}
                      placeholder={planTierPlaceholder}
                      onChange={(e) => {
                        const selected = tierOptions.find((tier) => tier?.value === e);
                        setNdata((d) => ({
                          ...d,
                          facility_tier_id: e,
                          facility_plan_type: selected?.tier_name ?? getFacilityPlanType(d),
                        }));
                      }}
                      options={tierOptions}
                      loading={loadingTiers}
                      style={{ width: "100%" }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility State:</Col>
                  <Col span={16}>
                    <Select
                      disabled={!edit}
                      value={n_data?.facility_state}
                      onChange={(value) =>
                        setNdata((d) => ({
                          ...d,
                          facility_state: value,
                        }))
                      }
                      options={states}
                      style={{ width: "100%" }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility City:</Col>
                  <Col span={16}>
                    <Input
                      disabled={!edit}
                      value={n_data?.facility_city}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_city: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility Phone:</Col>
                  <Col span={16}>
                    <Input
                      disabled={!edit}
                      value={n_data?.facility_phone}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_phone: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility Email:</Col>
                  <Col span={16}>
                    {edit ? <Input
                      // disabled={!edit}
                      value={n_data?.facility_email}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_email: e.target.value,
                        }))
                      }
                    />
                      : <InputCopy
                        // disabled={!edit}
                        value={n_data?.facility_email}
                        onChange={(e) =>
                          setNdata((d) => ({
                            ...d,
                            facility_email: e.target.value,
                          }))
                        }
                      />}
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Facility Address:</Col>
                  <Col span={16}>
                    <TextArea
                      disabled={!edit}
                      value={n_data?.facility_address}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_address: e.target.value,
                        }))
                      }
                      rows={3}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Constact Person:</Col>
                  <Col span={16}>
                    <Input
                      disabled={!edit}
                      value={n_data?.facility_designation}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_designation: e.target.value,
                        }))
                      }
                      rows={3}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Regitration Onboarded:</Col>
                  <Col span={16}>
                    <Input
                      disabled
                      value={n_data?.facility_date}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_date: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>Onboarded By:</Col>
                  <Col span={16}>
                    <Input
                      disabled
                      value={n_data?.facility_staff_id}
                      onChange={(e) =>
                        setNdata((d) => ({
                          ...d,
                          facility_staff_id: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
              </Col>
              {/* <Col span={24}>
                <Row justify="end" gutter={[16, 16]}>
                  <Col
                    style={{ textAlign: "right", alignContent: "flex-end" }}
                  > */}
              {/* {user &&
                       ? (
                      <>
                        {edit ? (
                          <CheckOutlined
                            onClick={() => setEdit(false)}
                            style={{ fontSize: 30, color: "blue" }}
                            title="Done"
                          />
                        ) : (
                          <EditOutlined
                            onClick={() => setEdit(true)}
                            style={{ fontSize: 30, color: "blue" }}
                          />
                        )}
                      </>
                    ) : null} */}
              {/* </Col>
                </Row>
              </Col> */}
            </Row>
          </Col>
        </Row>
      </Modal>

      <FacilityPassword
        open={showSetFacilityPassword}
        onCancel={() => setShowSetFacilityPassword(false)}
        data={data}
        onDone={command}
        modalZIndex={modalZIndex != null ? modalZIndex + 10 : undefined}
        modalClassName={modalClassName}
      />
    </>
  );
};

const FacilityPassword = props => {
  const [password, setPassword] = useState()

  const handleSavePassword = () => {
    const payload = {
      id: props.data.facility_id,
      password
    }

    myRequestObj('setFacilityPassword').post(payload)
      .then(resp => {
        const data = resp?.data;
        if (!data?.error) {
          notification.success({
            message: data?.message
          })
          props.onDone()
          props.onCancel()
        } else {
          notification.success({
            message: data?.msg || data?.message
          })
        }
      })
  }

  return <Modal
    title={'Set Password'}
    cancelText={'Close'}
    okText={'Save'}
    onCancel={props.onCancel}
    onOk={handleSavePassword}
    open={props.open}
    width={500}
    destroyOnClose
    zIndex={props.modalZIndex}
    rootClassName={props.modalClassName}
    className={props.modalClassName ? `${props.modalClassName}__dialog` : undefined}
  >
    <Row>
      <Col span={24}>Password</Col>
      <Col span={24}>
        <Input.Password value={password} onChange={e => setPassword(e.target.value)} width={'100%'} />
      </Col>
    </Row>
  </Modal>
}